<template>
  <div class="modal fade show custom-dialog" style="display: block" tabindex="-1" role="dialog">
    <div class="modal-dialog modal-dialog-centered" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ rule ? 'Edit' : 'Add' }} Discount Rule</h5>
          <button type="button" class="btn-close" :disabled="busy" @click="$emit('close')"></button>
        </div>
        <div class="modal-body">
          <p class="text-muted small">
            Applies at <strong>{{ firmName }}</strong> and every division and shop beneath it.
          </p>

          <div class="mb-3">
            <label class="form-label custom-label mb-0">Insurer</label>
            <Multiselect
              v-model="form.insurance"
              class="form-control custom-multiselect"
              placeholder="select an insurer"
              :options="insuranceOptions"
              :searchable="true"
              :disabled="busy || !!rule"
              label="name"
              value-prop="value"
            />
            <div v-if="errors.insurance" class="text-danger small mt-1">{{ errors.insurance }}</div>
          </div>

          <div class="mb-3">
            <label class="form-label custom-label mb-0">Discount %</label>
            <input
              type="number"
              class="form-control form-control-sm"
              v-model.number="form.percent"
              min="0.01"
              max="100"
              step="0.01"
              :disabled="busy"
            />
            <div v-if="errors.percent" class="text-danger small mt-1">{{ errors.percent }}</div>
          </div>

          <div class="mb-3">
            <label class="form-label custom-label mb-0">Applies to</label>
            <select class="form-select form-select-sm" v-model="form.scope" :disabled="busy">
              <option v-for="(label, value) in scopeLabels" :key="value" :value="value">{{ label }}</option>
            </select>
            <div class="form-text">
              Lines are ranked by price. "{{ scopeLabels.additional }}" - the most expensive line is billed at full price and only the other ADAS-L calibrations are discounted.
            </div>
          </div>

          <div class="mb-3">
            <label class="form-label custom-label mb-0">Effective from</label>
            <input type="date" class="form-control form-control-sm" v-model="form.effectiveOn" :disabled="busy" />
            <!-- <div class="form-text">Leave blank to start immediately.</div> -->
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-link btn-sm" :disabled="busy" @click="$emit('close')">Cancel</button>
          <button class="btn btn-primary btn-sm" :disabled="busy" @click="save">Save</button>
        </div>
      </div>
    </div>
  </div>
  <div class="modal-backdrop fade show"></div>
</template>
<script>
import { mapState, mapActions } from 'pinia'
import { lookupStore } from '@/stores/lookup'
import { pricingRuleStore } from '@/stores/pricingRule'
import Multiselect from '@vueform/multiselect'
import globals from '@/plugins/globals'
import { SCOPE_LABELS } from './scopeLabels'
import moment from 'moment'

export default {
  name: 'DialogPricingRule',
  components: { Multiselect },
  props: {
    // null when adding
    rule: { type: Object, default: null },
    firm: { type: Number, required: true },
    firmName: { type: String, default: '' }
  },
  emits: ['close', 'saved'],
  data() {
    return {
      form: {
        pricingRule: null,
        insurance: null,
        percent: null,
        scope: 'all',
        effectiveOn: null
      },
      errors: {},
      // the pre-filled date, so an unchanged edit keeps the stored start time
      originalEffectiveOn: null,
      scopeLabels: SCOPE_LABELS
    }
  },
  computed: {
    ...mapState(lookupStore, ['lookups']),
    ...mapState(pricingRuleStore, ['loading', 'rules']),
    busy() {
      return this.loading.saving
    },
    discountRuleInsurers() {
      return new Set(this.rules.filter((r) => r.ruleType === 'DiscountRule')
        .map((r) => parseInt(r.insurance))
      )
    },
    insuranceOptions() {
      if (!this.lookups || !this.lookups['Insurance']) return []
      const options = globals.sortArray(this.lookups['Insurance'], 'name')
      
      if (this.rule) {
        return options
      }

      return options.map((x) =>
        this.hasDiscountRule(x.value) ? { ...x, name: `${x.name} (insurer discount)`, disabled: true } : x
      )
    }
  },
  created() {
    if (this.rule) {
      this.form = {
        pricingRule: this.rule.pricingRule,
        insurance: this.rule.insurance,
        percent: this.rule.percent,
        scope: this.rule.scope || 'all',
        // EffectiveOn is a date stored at midnight +00:00; parseZone reads it without shifting to local time.
        effectiveOn: this.rule.effectiveOn ? moment.parseZone(this.rule.effectiveOn).format('YYYY-MM-DD') : null
      }
      this.originalEffectiveOn = this.form.effectiveOn
    } else {
      // Prefill today; left unchanged it's sent as null, so PricingRule_Save starts the rule now.
      this.form.effectiveOn = moment().format('YYYY-MM-DD')
      this.originalEffectiveOn = this.form.effectiveOn
    }
  },
  methods: {
    ...mapActions(pricingRuleStore, ['saveRule']),
    validate() {
      this.errors = {}
      if (!this.form.insurance) {
        this.errors.insurance = 'Select an insurer.'
      } else if (!this.rule && this.hasDiscountRule(this.form.insurance)) {
        this.errors.insurance = `This insurer already has an insurer-mandated discount rule; a new rule cannot be added.`
      }
      if (this.form.percent === null || this.form.percent === '' || isNaN(this.form.percent)) {
        this.errors.percent = 'Enter a discount percentage.'
      } else if (this.form.percent <= 0 || this.form.percent > 100) {
        this.errors.percent = 'Discount must be greater than 0 and at most 100.'
      }
      return Object.keys(this.errors).length === 0
    },
    hasDiscountRule(insurance) {
      return this.discountRuleInsurers.has(parseInt(insurance))
    },
    async save() {
      if (!this.validate()) {
        return
      }
      // Only one active rule per insurer at a location: PricingRule_Save retires the
      // existing one when a new one is added, so confirm before replacing it.
      if (!this.rule) {
        const existing = this.rules.find(
          (r) => r.ruleType !== 'DiscountRule' && parseInt(r.insurance) === parseInt(this.form.insurance)
        )
        if (existing) {
          const name = existing.insuranceName || `insurer ${existing.insurance}`
          if (!window.confirm(`${name} already has an active rule. Do you want to replace it?`)) {
            return
          }
        }
      }
      const effectiveOn =
        this.form.effectiveOn && (!this.rule || this.form.effectiveOn !== this.originalEffectiveOn)
          ? moment.utc(this.form.effectiveOn, 'YYYY-MM-DD').format()
          : null
      await this.saveRule({
        pricingRule: this.form.pricingRule || 0,
        firm: this.firm,
        insurance: parseInt(this.form.insurance),
        percent: parseFloat(this.form.percent),
        scope: this.form.scope,
        effectiveOn
      })
      this.$emit('saved')
    }
  }
}
</script>
